import type { SnakeCaseToCamelCase } from './cases';
import { snakeCaseToCamelCase } from './cases';

type AppErrorCodeConfig = {
  /**
   * The internal code for this type of error. Try to be
   * as granular as possible.
   **/
  code: string;
  /**
   * Whether it is worth retrying the action that caused
   * the error i.e. an intermittent fault.
   **/
  retryable: boolean;
  /**
   * Whether this error is expected to happen in normal
   * operation. Unexpected errors will be sent to Sentry.
   **/
  expected: boolean;
  /**
   * The HTTP status code that should be returned when this
   * error is encountered in a REST endpoint.
   **/
  httpStatusCode: number;
};

/**
 * Please extend this list with new error codes for different
 * error scenarios in the application. Try to be as specific
 * as possible.
 */
const APP_ERROR_CODES = [
  {
    code: 'AUTHENTICATION_REQUIRED',
    retryable: false,
    expected: true,
    httpStatusCode: 401,
  },
  {
    code: 'INSUFFICIENT_PERMISSIONS',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'FEATURE_DISABLED',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'FEATURE_RETIRED',
    retryable: false,
    expected: true,
    httpStatusCode: 410,
  },
  {
    code: 'MCP_FIELD_DISABLED',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'UNAVAILABLE_IN_PREVIEW',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'NOT_FOUND',
    retryable: false,
    expected: true,
    httpStatusCode: 404,
  },
  {
    code: 'INVALID_ID',
    retryable: false,
    expected: false,
    httpStatusCode: 422,
  },
  {
    code: 'NOT_IMPLEMENTED',
    retryable: false,
    expected: false,
    httpStatusCode: 501,
  },
  {
    code: 'CONFIG_ERROR',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    // This error is used when a system assertion fails - for example, when something we assume to be true about the data in all cases is false.
    code: 'SYSTEM_ASSERTION_FAILED',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'SCHEMA_VALIDATION_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 422,
  },
  {
    code: 'BAD_REQUEST',
    retryable: false,
    expected: true,
    httpStatusCode: 400,
  },
  {
    code: 'OPEN_SOLAR_API_ERROR',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'ENA_API_ERROR',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'CLONE_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 409,
  },
  {
    code: 'SUBDOMAIN_NOT_AVAILABLE',
    retryable: false,
    expected: true,
    httpStatusCode: 409,
  },
  {
    code: 'SUBDOMAIN_CREATION_FAILED',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'CONFLICT',
    retryable: false,
    expected: true,
    httpStatusCode: 409,
  },
  {
    code: 'UNSUPPORTED_BY_VERSION',
    retryable: false,
    expected: true,
    httpStatusCode: 409,
  },
  {
    code: 'BUSINESS_VALIDATION_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 422,
  },
  {
    code: 'KNEX_QUERY_IN_KYSELY_TRANSACTION',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'NO_EMAIL_ADDRESSES',
    retryable: false,
    expected: true,
    httpStatusCode: 400,
  },
  {
    code: 'INTEGRATION_ERROR',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'INTEGRATION_TIMEOUT',
    retryable: true,
    expected: true,
    httpStatusCode: 504,
  },
  {
    code: 'EXTERNAL_WEBHOOK_ERROR_RESPONSE',
    retryable: true,
    expected: true,
    httpStatusCode: 500,
  },
  {
    code: 'EMAIL_INACTIVE',
    retryable: false,
    expected: true,
    httpStatusCode: 400,
  },
  {
    code: 'EMAIL_SERVER_NOT_CONFIGURED',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'UNABLE_TO_INFER_FILETYPE',
    retryable: false,
    expected: true,
    httpStatusCode: 400,
  },
  {
    code: 'IMAGE_CONVERSION_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 422,
  },
  {
    code: 'PDF_GENERATION_FAILED',
    retryable: true,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'UNSUPPORTED_FILE_TYPE',
    retryable: false,
    expected: true,
    httpStatusCode: 415,
  },
  {
    code: 'ACCESS_POLICY_CHECK_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'ACCOUNT_SCOPE_CHECK_FAILED',
    retryable: false,
    expected: true,
    httpStatusCode: 403,
  },
  {
    code: 'ACCOUNT_SCOPE_COVERAGE_MISSING',
    retryable: false,
    expected: false,
    httpStatusCode: 500,
  },
  {
    code: 'STRIPE_ERROR',
    retryable: false,
    expected: true,
    httpStatusCode: 502,
  },
  {
    code: 'CITY_PLUMBING_OAUTH_FAILED',
    retryable: true,
    expected: false,
    httpStatusCode: 502,
  },
] as const satisfies AppErrorCodeConfig[];

export type AppErrorCode = (typeof APP_ERROR_CODES)[number]['code'];

const configsByCode = Object.fromEntries(
  APP_ERROR_CODES.map((config) => [config.code, config])
) as Record<AppErrorCode, AppErrorCodeConfig>;

type AppErrorOptions = {
  cause?: Error;
};

class AppError<Code extends AppErrorCode = AppErrorCode> extends Error {
  code: Code;
  userFacingMessage: string;
  stack: string;
  cause?: Error;

  constructor(
    code: Code,
    userFacingMessage: string,
    options: AppErrorOptions = {}
  ) {
    super(`${code} - ${userFacingMessage}`);
    this.name = 'AppError';
    this.code = code;
    this.userFacingMessage = userFacingMessage;

    if (options.cause) {
      this.cause = options.cause;
    }

    // Capture the stack trace if not already set.
    // `captureStackTrace` is a V8-only API; type it locally so this file needs
    // no `@types/node` when read from source by a JIT consumer.
    (
      Error as {
        captureStackTrace?: (target: object, constructor: unknown) => void;
      }
    ).captureStackTrace?.(this, appError);
    this.stack ??= '';
  }

  isRetryable(): boolean {
    return configsByCode[this.code].retryable;
  }

  isExpected(): boolean {
    return configsByCode[this.code].expected;
  }

  httpStatusCode(): number {
    return configsByCode[this.code].httpStatusCode;
  }

  toJSON(
    { includeStackTrace } = {
      includeStackTrace: true,
    }
  ) {
    return {
      code: this.code,
      userFacingMessage: this.userFacingMessage,
      retryable: this.isRetryable(),
      expected: this.isExpected(),
      // Stacktrace and cause are diagnostic: useful in trusted contexts (logs,
      // Sentry, local dev) but must not appear in HTTP responses we send to
      // external clients, since `cause.message` can carry SQL or other
      // upstream-error internals.
      stacktrace: includeStackTrace
        ? this.stack.split('\n').map((line) => line.trimStart())
        : undefined,
      cause: includeStackTrace && this.cause ? this.cause.message : undefined,
    };
  }
}

export type { AppError };

/**
 * Creates a new AppError instance, which can be thrown anywhere
 * in backend code (Koa, GraphQL, NestJS, scheduled tasks).
 */
function _appError<Code extends AppErrorCode = AppErrorCode>(
  code: Code,
  userFacingMessage: string,
  options: AppErrorOptions = {}
): AppError {
  return new AppError<Code>(code, userFacingMessage, options);
}

// This dynamically adds helper methods for each error code
// e.g. `appError.notFound()`, `appError.badRequest()`
for (const codeConfig of APP_ERROR_CODES) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  (_appError as any)[snakeCaseToCamelCase(codeConfig.code.toLowerCase())] =
    function (userFacingMessage: string, options: AppErrorOptions = {}) {
      return new AppError(codeConfig.code, userFacingMessage, options);
    };
}

type AppErrorConstructors = {
  [Code in AppErrorCode as SnakeCaseToCamelCase<Lowercase<Code>>]: (
    userFacingMessage: string,
    options?: AppErrorOptions
  ) => AppError<Code>;
};

export const appError = _appError as typeof _appError & AppErrorConstructors;

/**
 * A type guard to check if an error is an instance of AppError,
 * and optionally check against a list of error codes. This can be
 * used to handle specific error codes in a type-safe way.
 */
export function isAppError<const Code extends AppErrorCode = AppErrorCode>(
  error: unknown,
  codes?: readonly Code[]
): error is AppError<Code> {
  return (
    error instanceof AppError &&
    (codes === undefined || codes.some((code) => code === error.code))
  );
}
